import axios from "axios";
import { ip_back } from "../../config";
import {
  setCaptureQosContext,
  trackCaptureQosUpload,
} from "../../utils/captureQos";

const getToken = () => localStorage.getItem("token");
const headers = () => ({ headers: { Authorization: getToken() } });
const base = () => `${String(ip_back || "").replace(/\/?$/, "/")}`;

/** Club the signed-in account is currently administering or playing in. */
export function readCompetitionClubId() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const brand = JSON.parse(localStorage.getItem("brand_config") || "{}");
    const id =
      user?.club_id ||
      user?.club_details?.club_id ||
      user?.club?.club_id ||
      brand?.club_id ||
      "";
    return String(id || "").trim();
  } catch {
    return "";
  }
}

function withClub(params = {}) {
  const club_id = String(params.club_id || params.clubId || readCompetitionClubId() || "").trim();
  if (!club_id) return params;
  const next = { ...params, club_id };
  delete next.clubId;
  return next;
}

export async function fetchSeniorsTeams({ reform = false, lang = "en", clubId } = {}) {
  const res = await axios.get(`${base()}api/competitions/seniors/teams`, {
    ...headers(),
    params: withClub({ lang, clubId, ...(reform ? { reform: 1 } : {}) }),
  });
  return res.data?.data || null;
}

export async function reformSeniorsTeams({ lang = "en", clubId } = {}) {
  const res = await axios.post(
    `${base()}api/competitions/seniors/reform`,
    withClub({ lang, clubId }),
    headers()
  );
  return res.data?.data || null;
}

export async function benchCompetitionPlayer(
  competitionId,
  userId,
  { note = "", lang = "en" } = {}
) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/bench`,
    { user_id: userId, note, lang },
    headers()
  );
  return res.data?.data || null;
}

export async function unbenchCompetitionPlayer(
  competitionId,
  userId,
  { lang = "en" } = {}
) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/unbench`,
    { user_id: userId, lang },
    headers()
  );
  return res.data?.data || null;
}

export async function setCompetitionParticipation(
  competitionId,
  userId,
  status,
  { note = "" } = {}
) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/status`,
    { user_id: userId, status, note },
    headers()
  );
  return res.data?.data || null;
}

export async function setClubChampionshipTeamCount(count, { clubId } = {}) {
  const res = await axios.post(
    `${base()}api/competitions/club/team-count`,
    withClub({ championship_team_count: count, clubId }),
    headers()
  );
  return res.data?.data || null;
}

export async function listCompetitions({ clubId } = {}) {
  const res = await axios.get(`${base()}api/competitions`, {
    ...headers(),
    params: withClub({ clubId }),
  });
  return res.data?.data || [];
}

export async function createCompetition(body) {
  const res = await axios.post(
    `${base()}api/competitions`,
    withClub(body || {}),
    headers()
  );
  return res.data?.data || null;
}

export async function updateCompetition(competitionId, body) {
  const res = await axios.patch(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function importLeagueSpielplan({
  file = null,
  sourceUrl = "",
  calendarText = "",
} = {}) {
  const token = getToken();
  if (file) {
    const form = new FormData();
    form.append("file", file);
    if (sourceUrl) form.append("source_url", sourceUrl);
    const res = await axios.post(`${base()}api/competitions/league/import`, form, {
      headers: { Authorization: token },
    });
    return res.data?.data || null;
  }
  const res = await axios.post(
    `${base()}api/competitions/league/import`,
    { source_url: sourceUrl || undefined, calendar_text: calendarText || undefined },
    headers()
  );
  return res.data?.data || null;
}

/** Coach/admin: sync official FLTT results; feuille PDFs only for this club. */
export async function syncFlttMatchdayResults({
  sourceUrl = "",
  dryRun = false,
  matchDate = "",
  auto = false,
} = {}) {
  const res = await axios.post(
    `${base()}api/competitions/league/sync-results`,
    {
      ...withClub({}),
      source_url: sourceUrl || undefined,
      dry_run: dryRun || undefined,
      match_date: matchDate || undefined,
      auto: auto || undefined,
    },
    { ...headers(), timeout: 180000 }
  );
  return res.data?.data || null;
}

export async function fetchLeagueSeason({ clubId } = {}) {
  const res = await axios.get(`${base()}api/competitions/league/season`, {
    ...headers(),
    params: withClub({ clubId }),
  });
  return res.data?.data || null;
}

export async function fetchLeagueFixtures(params = {}) {
  const res = await axios.get(`${base()}api/competitions/league/fixtures`, {
    ...headers(),
    params: withClub(params),
  });
  return res.data?.data || null;
}

export async function fetchMyLeagueSchedule({
  lang = "en",
  clubId,
  lite = false,
  scope,
} = {}) {
  const res = await axios.get(`${base()}api/competitions/league/my-schedule`, {
    ...headers(),
    params: withClub({
      lang,
      clubId,
      ...(lite ? { lite: 1 } : {}),
      ...(scope ? { scope } : {}),
    }),
  });
  return res.data?.data || null;
}

/** Presence roster, loaded after the calendar is on screen. */
export async function fetchMyLeagueRoster({ lang = "en", clubId } = {}) {
  return fetchMyLeagueSchedule({ lang, clubId, scope: "roster" });
}

export async function setLeagueFixtureRsvp(
  fixtureId,
  status,
  { note = "", playerUserId } = {}
) {
  const res = await axios.post(
    `${base()}api/competitions/league/fixtures/${encodeURIComponent(fixtureId)}/rsvp`,
    { status, note, player_user_id: playerUserId || undefined },
    headers()
  );
  return res.data?.data || null;
}

export async function fetchLeagueFixtureRsvps(fixtureId) {
  const res = await axios.get(
    `${base()}api/competitions/league/fixtures/${encodeURIComponent(fixtureId)}/rsvps`,
    headers()
  );
  return res.data?.data || [];
}

export async function fetchLeagueFixtureOpponentPreview(fixtureId) {
  const res = await axios.get(
    `${base()}api/competitions/league/fixtures/${encodeURIComponent(
      fixtureId
    )}/opponent-preview`,
    headers()
  );
  return res.data?.data || null;
}

/** List FLTT evenements tournaments (auto-refreshes stale cache). */
export async function fetchFlttTournaments({ refresh = false, cached = false } = {}) {
  const params = {};
  if (refresh) params.refresh = 1;
  else if (cached) params.cached = 1;
  const res = await axios.get(`${base()}api/competitions/fltt-tournaments`, {
    ...headers(),
    params: Object.keys(params).length ? params : undefined,
    timeout: cached ? 20000 : 120000,
  });
  return res.data?.data || { tournaments: [] };
}

/** Coach/admin: force sync from https://www.fltt.lu/evenements */
export async function syncFlttTournaments({ sourceUrl = "" } = {}) {
  const res = await axios.post(
    `${base()}api/competitions/fltt-tournaments/sync`,
    { source_url: sourceUrl || undefined },
    { ...headers(), timeout: 120000 }
  );
  return res.data?.data || null;
}

/** Coupe Félix Felten (team cup) for Luxembourg clubs. */
export async function fetchFeltenCup({ clubId, refresh = false, cached = false } = {}) {
  const res = await axios.get(`${base()}api/competitions/fltt-cups/felten`, {
    ...headers(),
    params: {
      ...(clubId ? { club_id: clubId } : {}),
      ...(refresh ? { refresh: 1 } : {}),
      ...(!refresh && cached ? { cached: 1 } : {}),
    },
    timeout: cached ? 20000 : 180000,
  });
  return res.data?.data || { registered: false };
}

/** Coach/admin: force re-crawl Coupe Felten groups from FLTT. */
export async function syncFeltenCup({ clubId } = {}) {
  const res = await axios.post(
    `${base()}api/competitions/fltt-cups/felten/sync`,
    withClub({ clubId }),
    { ...headers(), timeout: 180000 }
  );
  return res.data?.data || null;
}

/** Open a recordable match room for the Felten next match (or a given fixture). */
export async function openFeltenCupMatch({
  clubId,
  fixtureId,
  fixtureKey,
  useNext = true,
} = {}) {
  const res = await axios.post(
    `${base()}api/competitions/fltt-cups/felten/match`,
    withClub({
      clubId,
      fixture_id: fixtureId || undefined,
      fixture_key: fixtureKey || undefined,
      use_next: useNext,
    }),
    { ...headers(), timeout: 60000 }
  );
  return res.data?.data || null;
}

/** Player (or coach for a player): notify participation for categories. */
export async function setFlttTournamentRsvp(
  tournamentId,
  status,
  { categoryIds = null, categoryId = null, note = "", playerUserId } = {}
) {
  const res = await axios.post(
    `${base()}api/competitions/fltt-tournaments/${encodeURIComponent(tournamentId)}/rsvp`,
    {
      status,
      category_ids: categoryIds || undefined,
      category_id: categoryId || undefined,
      note: note || undefined,
      player_user_id: playerUserId || undefined,
    },
    headers()
  );
  return res.data?.data || null;
}

/**
 * Sync FLTT apps STD results for this club.
 * `ensurePast` auto-loads use the DB cache; the API pulls from FLTT at most once
 * per club per week unless `force` is set (staff refresh).
 */
export async function syncFlttStdResults({
  competitionIds = null,
  maxCompetitions = undefined,
  eventDates = null,
  ensurePast = false,
  force = false,
} = {}) {
  const res = await axios.post(
    `${base()}api/competitions/fltt-std/sync`,
    {
      competition_ids: competitionIds || undefined,
      max_competitions: maxCompetitions,
      event_dates: Array.isArray(eventDates) ? eventDates : undefined,
      ensure_past: ensurePast || undefined,
      force: force || undefined,
    },
    { ...headers(), timeout: 180000 }
  );
  return res.data?.data || null;
}

/** List scraped / manual STD tournament singles matches. */
export async function fetchFlttStdMatches({
  mine = true,
  eventDate = "",
  limit = 200,
} = {}) {
  const res = await axios.get(`${base()}api/competitions/fltt-std/matches`, {
    ...headers(),
    params: {
      mine: mine ? 1 : undefined,
      event_date: eventDate || undefined,
      limit,
    },
  });
  return res.data?.data || [];
}

/** Club tournament results (podium + matches) from FLTT apps sync. */
export async function fetchFlttStdTournamentResults({
  tournamentId = "",
  eventDate = "",
} = {}) {
  const res = await axios.get(`${base()}api/competitions/fltt-std/results`, {
    ...headers(),
    params: {
      tournament_id: tournamentId || undefined,
      event_date: eventDate || undefined,
    },
  });
  return res.data?.data || [];
}

export async function createFlttStdMatch(body) {
  const res = await axios.post(
    `${base()}api/competitions/fltt-std/matches`,
    body || {},
    headers()
  );
  return res.data?.data || null;
}

export async function updateFlttStdMatch(stdMatchId, body) {
  const res = await axios.patch(
    `${base()}api/competitions/fltt-std/matches/${encodeURIComponent(stdMatchId)}`,
    body || {},
    headers()
  );
  return res.data?.data || null;
}

export async function fetchFlttStdLinkableVideos({
  eventDate = "",
  mine = true,
} = {}) {
  const res = await axios.get(`${base()}api/competitions/fltt-std/videos`, {
    ...headers(),
    params: {
      event_date: eventDate || undefined,
      mine: mine ? 1 : undefined,
    },
  });
  return res.data?.data || [];
}

export async function uploadFlttStdLocalMedia(file, fields = {}) {
  const form = new FormData();
  form.append("file", file);
  Object.entries(fields).forEach(([k, v]) => {
    if (v != null && v !== "") form.append(k, String(v));
  });
  const res = await axios.post(`${base()}api/competitions/fltt-std/media`, form, {
    headers: { Authorization: getToken() },
    timeout: 900000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
  });
  return res.data?.data || null;
}

export async function deleteFlttStdGalleryMedia(
  mediaId,
  { stdCompetitionId = null, tournamentId = null } = {}
) {
  const res = await axios.delete(
    `${base()}api/competitions/fltt-std/media/${encodeURIComponent(mediaId)}`,
    {
      ...headers(),
      data: {
        std_competition_id: stdCompetitionId || undefined,
        tournament_id: tournamentId || undefined,
      },
    }
  );
  return res.data?.data || null;
}

export async function listFlttStdMatchNotes(stdMatchId) {
  const res = await axios.get(
    `${base()}api/competitions/fltt-std/matches/${encodeURIComponent(
      stdMatchId
    )}/notes`,
    headers()
  );
  return res.data?.data || [];
}

export async function createFlttStdMatchNote(stdMatchId, body) {
  const hasFile = body?.file instanceof Blob || body?.file instanceof File;
  if (hasFile) {
    const form = new FormData();
    form.append("file", body.file);
    Object.entries(body).forEach(([k, v]) => {
      if (k === "file" || v == null || v === "") return;
      form.append(k, String(v));
    });
    const res = await axios.post(
      `${base()}api/competitions/fltt-std/matches/${encodeURIComponent(
        stdMatchId
      )}/notes`,
      form,
      {
        headers: { Authorization: getToken() },
        timeout: 120000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      }
    );
    return res.data?.data || null;
  }
  const res = await axios.post(
    `${base()}api/competitions/fltt-std/matches/${encodeURIComponent(
      stdMatchId
    )}/notes`,
    body || {},
    headers()
  );
  return res.data?.data || null;
}

export async function deleteFlttStdMatchNote(stdMatchId, noteId) {
  const res = await axios.delete(
    `${base()}api/competitions/fltt-std/matches/${encodeURIComponent(
      stdMatchId
    )}/notes/${encodeURIComponent(noteId)}`,
    headers()
  );
  return res.data?.data || null;
}

export async function listChampionshipMatches({ clubId } = {}) {
  const res = await axios.get(`${base()}api/competitions/matches`, {
    ...headers(),
    params: withClub({ clubId }),
  });
  return res.data?.data || [];
}

export async function openPlayerTournamentMatch(tournamentId, categoryId, body = {}) {
  const res = await axios.post(
    `${base()}api/competitions/player-matches`,
    {
      tournament_id: tournamentId,
      category_id: categoryId || null,
      ...body,
    },
    headers()
  );
  return res.data?.data || null;
}

export async function listPlayerTournamentMatches(tournamentId) {
  const res = await axios.get(`${base()}api/competitions/player-matches`, {
    ...headers(),
    params: tournamentId ? { tournament_id: tournamentId } : {},
  });
  return res.data?.data || [];
}

export async function updatePlayerTournamentMatch(matchId, body) {
  const res = await axios.patch(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/player`,
    body || {},
    headers()
  );
  return res.data?.data || null;
}

export async function refreshPlayerScorerGrant(matchId) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/scorer/grant`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function redeemPlayerScorerCode(code) {
  const res = await axios.post(
    `${base()}api/competitions/player-matches/redeem`,
    { code },
    headers()
  );
  return res.data?.data || null;
}

export async function openChampionshipMatch(fixtureId) {
  const res = await axios.post(
    `${base()}api/competitions/league/fixtures/${encodeURIComponent(fixtureId)}/match`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function fetchChampionshipMatch(matchId) {
  const res = await axios.get(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}`,
    headers()
  );
  return res.data?.data || null;
}

export async function postChampionshipSheetPhoto(matchId, imageJpegBase64) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/sheet-photo`,
    { image_jpeg_base64: imageJpegBase64 },
    headers()
  );
  return res.data?.data || null;
}

export async function saveChampionshipLineup(matchId, body) {
  const res = await axios.patch(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/lineup`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function setChampionshipOpponentAvailability(matchId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(
      matchId
    )}/opponent-availability`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function claimChampionshipScorer(matchId) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/scorer/claim`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function releaseChampionshipScorer(matchId) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/scorer/release`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function assignChampionshipRubberTable(
  matchId,
  rubberId,
  body = {}
) {
  const res = await axios.patch(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function startChampionshipRubber(matchId, rubberId, body = {}) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/start`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function startChampionshipSet(matchId, rubberId, body = {}) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/start-set`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function syncChampionshipMediaClock(matchId, rubberId, body = {}) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/media-sync`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function postChampionshipPoint(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/point`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function undoChampionshipPoint(matchId, rubberId) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/undo`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function removeChampionshipPoint(matchId, rubberId, winnerSide) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/remove-point`,
    { winner_side: winnerSide },
    headers()
  );
  return res.data?.data || null;
}

export async function setChampionshipScore(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/set-score`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function setChampionshipFinalResult(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/final-result`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function clearChampionshipRubberPoints(matchId, rubberId) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/clear-points`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function postChampionshipReviewPoint(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/review-point`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function postChampionshipReviewReaction(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/reactions`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function probeChampionshipScore(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/score-probe`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function fingerprintChampionshipScore(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/score-fingerprint`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function scanChampionshipScoreSegment(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/score-scan`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function labelChampionshipScoreVideo(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/score-label`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function getChampionshipScoreLabel(matchId, rubberId, mediaId) {
  const res = await axios.get(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/score-label`,
    {
      ...headers(),
      params: { media_id: mediaId },
    }
  );
  return res.data?.data || null;
}

export async function postChampionshipScoreOcr(matchId, rubberId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/rubbers/${encodeURIComponent(rubberId)}/score-ocr`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function annotateChampionshipPoint(matchId, pointId, body) {
  const res = await axios.patch(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/points/${encodeURIComponent(pointId)}`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function claimChampionshipCamera(matchId, body) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/camera`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function releaseChampionshipCamera(matchId, body = {}) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/camera/release`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function completeChampionshipMatch(matchId) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/complete`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function resetChampionshipMatch(matchId) {
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/reset`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function uploadChampionshipMedia(matchId, file, fields = {}, options = {}) {
  setCaptureQosContext({
    flow: "championship",
    session_id: String(matchId || ""),
    match_id: String(matchId || ""),
    capture_id: fields?.capture_id ? String(fields.capture_id) : null,
  });
  const bytes =
    (file && typeof file.size === "number" ? file.size : 0) || 0;
  const filename =
    options.filename ||
    (typeof file?.name === "string" && file.name) ||
    "clip.mp4";
  return trackCaptureQosUpload(
    {
      bytes,
      capture_id: fields?.capture_id || null,
      clip_id: fields?.capture_id || null,
      segment_index: fields?.t_ms != null ? Number(fields.t_ms) : null,
    },
    async () => {
      const form = new FormData();
      form.append("file", file, filename);
      Object.entries(fields).forEach(([k, v]) => {
        if (v != null && v !== "") form.append(k, String(v));
      });
      try {
        const res = await axios.post(
          `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/media`,
          form,
          {
            headers: { Authorization: getToken() },
            // Long mobile uploads (match sets) — nginx also allows 900s.
            timeout: options.timeout ?? 900000,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            onUploadProgress: options.onUploadProgress,
            signal: options.signal,
          }
        );
        return res.data?.data || null;
      } catch (err) {
        if (err?.response?.status != null) {
          err.status = err.response.status;
        }
        throw err;
      }
    }
  );
}

/** Mix a sidecar mic file into a clip that was uploaded without audio. */
export async function uploadChampionshipMediaAudio(matchId, mediaId, blob) {
  if (!matchId || !mediaId || !blob || blob.size < 256) return null;
  const form = new FormData();
  const ext = String(blob.type || "").includes("webm") ? "webm" : "m4a";
  form.append("audio", blob, `audio.${ext}`);
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/media/${encodeURIComponent(mediaId)}/audio`,
    form,
    {
      headers: { Authorization: getToken() },
      timeout: 120000,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    }
  );
  return res.data?.data || null;
}

export async function listChampionshipPlayerNotes(matchId) {
  const res = await axios.get(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/notes`,
    headers()
  );
  return res.data?.data || [];
}

export async function createChampionshipPlayerNote(matchId, body) {
  const hasFile = body?.file instanceof Blob || body?.file instanceof File;
  if (hasFile) {
    const form = new FormData();
    form.append("file", body.file);
    Object.entries(body).forEach(([k, v]) => {
      if (k === "file" || v == null || v === "") return;
      form.append(k, String(v));
    });
    const res = await axios.post(
      `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/notes`,
      form,
      {
        headers: { Authorization: getToken() },
        timeout: 120000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      }
    );
    return res.data?.data || null;
  }
  const res = await axios.post(
    `${base()}api/competitions/matches/${encodeURIComponent(matchId)}/notes`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function updateChampionshipPlayerNote(matchId, noteId, body) {
  const res = await axios.patch(
    `${base()}api/competitions/matches/${encodeURIComponent(
      matchId
    )}/notes/${encodeURIComponent(noteId)}`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function deleteChampionshipPlayerNote(matchId, noteId) {
  const res = await axios.delete(
    `${base()}api/competitions/matches/${encodeURIComponent(
      matchId
    )}/notes/${encodeURIComponent(noteId)}`,
    headers()
  );
  return res.data?.data || null;
}

/** Private notes + shared feedback about an opponent for future prep. */
export async function listChampionshipNotesAboutPlayer(params = {}) {
  const res = await axios.get(
    `${base()}api/competitions/matches/notes/about`,
    {
      ...headers(),
      params: {
        user_id: params.user_id || undefined,
        player_id: params.player_id || undefined,
        display_name: params.display_name || undefined,
        club_id: params.club_id || undefined,
        limit: params.limit || undefined,
      },
    }
  );
  return res.data?.data || [];
}

export async function searchTournamentPlayers(q, { limit = 20 } = {}) {
  const res = await axios.get(`${base()}api/competitions/tournament/player-search`, {
    ...headers(),
    params: { q, limit },
  });
  return res.data?.data || [];
}

export async function fetchTournamentClubPlayers() {
  const res = await axios.get(`${base()}api/competitions/tournament/club-players`, headers());
  return res.data?.data || [];
}

export async function fetchTournamentTables() {
  const res = await axios.get(`${base()}api/competitions/tournament/tables`, headers());
  return res.data?.data || [];
}

export async function setupAndGenerateTournament(body, competitionId) {
  if (competitionId) {
    const res = await axios.put(
      `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament`,
      body,
      headers()
    );
    return res.data?.data || null;
  }
  const res = await axios.post(`${base()}api/competitions/tournament`, body, headers());
  return res.data?.data || null;
}

export async function fetchTournament(competitionId) {
  const res = await axios.get(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament`,
    headers()
  );
  return res.data?.data || null;
}

export async function setTournamentPresence(competitionId, body) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/presence`,
    body,
    headers()
  );
  return res.data?.data || null;
}

export async function saveTournamentSettings(body, competitionId) {
  if (competitionId) {
    const res = await axios.put(
      `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/settings`,
      body,
      headers()
    );
    return res.data?.data || null;
  }
  const res = await axios.post(`${base()}api/competitions/tournament/settings`, body, headers());
  return res.data?.data || null;
}

export async function publishTournament(competitionId) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/publish`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function closeAndGenerateTournament(competitionId) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/close-and-generate`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function listClubTournaments() {
  const res = await axios.get(`${base()}api/competitions/club-tournaments`, headers());
  return res.data?.data || [];
}

export async function registerForClubTournament(competitionId, categoryIds) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/register`,
    { category_ids: categoryIds },
    headers()
  );
  return res.data?.data || null;
}

export async function withdrawClubTournament(competitionId) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/withdraw`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function reportTournamentScore(competitionId, matchId, sets) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/matches/${encodeURIComponent(matchId)}/score`,
    { sets },
    headers()
  );
  return res.data?.data || null;
}

export async function openClubTournamentLiveRoom(competitionId, matchId) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/matches/${encodeURIComponent(matchId)}/live-room`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function readTournamentScorePhoto(competitionId, matchId, imageJpegBase64) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/matches/${encodeURIComponent(matchId)}/score-photo`,
    { image_jpeg_base64: imageJpegBase64 },
    headers()
  );
  return res.data?.data || null;
}

export async function markTournamentPaid(competitionId, playerId) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/players/${encodeURIComponent(playerId)}/paid`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function declineTournamentPlayer(competitionId, playerId) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/players/${encodeURIComponent(playerId)}/decline`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function startTournamentCheckout(competitionId, tournamentPlayerId) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/payments/stripe`,
    { tournament_player_id: tournamentPlayerId },
    headers()
  );
  return res.data?.data || null;
}

export async function confirmTournamentPayment(competitionId, paymentId) {
  const res = await axios.post(
    `${base()}api/competitions/${encodeURIComponent(competitionId)}/tournament/payments/${encodeURIComponent(paymentId)}/confirm`,
    {},
    headers()
  );
  return res.data?.data || null;
}

export async function fetchPublicTournament(token) {
  const res = await axios.get(`${base()}api/public/tournament/${encodeURIComponent(token)}`);
  return res.data?.data || null;
}

export async function registerPublicTournament(token, body) {
  const res = await axios.post(
    `${base()}api/public/tournament/${encodeURIComponent(token)}`,
    body
  );
  return res.data?.data || null;
}

export async function startPublicTournamentCheckout(token, tournamentPlayerId) {
  const res = await axios.post(
    `${base()}api/public/tournament/${encodeURIComponent(token)}/payments/stripe`,
    { tournament_player_id: tournamentPlayerId }
  );
  return res.data?.data || null;
}

export async function confirmPublicTournamentPayment(token, paymentId) {
  const res = await axios.post(
    `${base()}api/public/tournament/${encodeURIComponent(token)}/payments/${encodeURIComponent(paymentId)}/confirm`,
    {}
  );
  return res.data?.data || null;
}

